import { useCallback } from "react";
import { OBJECT_BASE_SIZE } from "components/features/ProgressReportViewer/utils";
import { ZOOM_CONFIG } from "../../FloorPlanPanelNew/constants";
import { computeZoomToObject } from "./utils";
import { computeZoomToBbox } from "../../FloorPlanPanelNew/utils/computeZoomToBbox";
import type {
  ZoomToObject,
  ZoomToBbox,
  ZoomToElements,
} from "../../FloorPlanPanelNew/types";
import type { UseZoomActionsArgs, UseZoomActionsResult } from "./types";

export const useZoomActions = ({
  containerRef,
  pageWidth,
  pageHeight,
  setZoomLevel,
  setPanOffset,
  minZoomLevel,
}: UseZoomActionsArgs): UseZoomActionsResult => {
  const zoomToObject: ZoomToObject = useCallback(
    (obj) => {
      if (!obj || !containerRef.current) return;

      const containerRect = containerRef.current.getBoundingClientRect();
      const { zoom, panX, panY } = computeZoomToObject(
        obj,
        containerRect.width,
        containerRect.height,
      );

      setZoomLevel(zoom);
      setPanOffset({ x: panX, y: panY });
    },
    [containerRef, setZoomLevel, setPanOffset],
  );

  const zoomToBbox: ZoomToBbox = useCallback(
    (bbox) => {
      if (!containerRef.current) return;

      const containerRect = containerRef.current.getBoundingClientRect();
      const { zoom, panX, panY } = computeZoomToBbox({
        bbox,
        containerWidth: containerRect.width,
        containerHeight: containerRect.height,
        minZoom: minZoomLevel.current,
        maxZoom: ZOOM_CONFIG.MANUAL_MAX,
      });

      setZoomLevel(zoom);
      setPanOffset({ x: panX, y: panY });
    },
    [containerRef, minZoomLevel, setZoomLevel, setPanOffset],
  );

  const zoomToFit = useCallback(() => {
    const container = containerRef.current;
    if (!container || pageWidth <= 0 || pageHeight <= 0) return;

    const containerRect = container.getBoundingClientRect();
    const scaleX = containerRect.width / pageWidth;
    const scaleY = containerRect.height / pageHeight;
    const fitZoom = Math.min(scaleX, scaleY, 1);

    const renderedWidth = pageWidth * fitZoom;
    const renderedHeight = pageHeight * fitZoom;
    const centerX = (containerRect.width - renderedWidth) / 2;
    const centerY = (containerRect.height - renderedHeight) / 2;

    minZoomLevel.current = fitZoom;
    setZoomLevel(fitZoom);
    setPanOffset({ x: centerX, y: centerY });
  }, [
    containerRef,
    pageWidth,
    pageHeight,
    setZoomLevel,
    setPanOffset,
    minZoomLevel,
  ]);

  const zoomToElements: ZoomToElements = useCallback(
    ({ elements, padding = OBJECT_BASE_SIZE }) => {
      if (!elements || elements.length === 0 || !containerRef.current) return;

      const containerRect = containerRef.current.getBoundingClientRect();

      let minX = Infinity;
      let maxX = -Infinity;
      let minY = Infinity;
      let maxY = -Infinity;

      elements.forEach((element) => {
        minX = Math.min(minX, element.x);
        maxX = Math.max(maxX, element.x);
        minY = Math.min(minY, element.y);
        maxY = Math.max(maxY, element.y);
      });

      const boxWidth = maxX - minX;
      const boxHeight = maxY - minY;
      const boxCenterX = (minX + maxX) / 2;
      const boxCenterY = (minY + maxY) / 2;

      const zoomX =
        (containerRect.width - padding * 2) / (boxWidth + padding * 2);
      const zoomY =
        (containerRect.height - padding * 2) / (boxHeight + padding * 2);
      const targetZoom = Math.min(zoomX, zoomY);
      const clampedZoom = Math.max(
        ZOOM_CONFIG.MIN,
        Math.min(ZOOM_CONFIG.MAX, targetZoom),
      );

      const containerCenterX = containerRect.width / 2;
      const containerCenterY = containerRect.height / 2;

      setZoomLevel(clampedZoom);
      setPanOffset({
        x: containerCenterX - boxCenterX * clampedZoom,
        y: containerCenterY - boxCenterY * clampedZoom,
      });
    },
    [containerRef, setZoomLevel, setPanOffset],
  );

  return { zoomToFit, zoomToObject, zoomToBbox, zoomToElements };
};
